/**
 * Manual trading terminal — request/response contracts.
 *
 * The dashboard Terminal uses three endpoints:
 *   POST   /api/manual-trade/execute   — market fan-out to N accounts
 *   POST   /api/manual-trade/pending   — queue one pending order
 *   DELETE /api/manual-trade/pending/:id
 *   GET    /api/manual-trade/pending?pair=<slug>
 *   POST   /api/manual-trade/close     — close a single ticket
 *   POST   /api/manual-trade/modify    — modify SL/TP on a single ticket
 *
 * Execute and modify/close reuse the existing `command:open / command:close
 * / command:modify` WS frames already handled by the bot. Pending orders
 * are NOT placed at the broker — the worker holds them and fires
 * `command:open` when the live broker-radar tick matches the trigger.
 */

import { z } from "zod";

/** One leg of a multi-account fan-out. */
export const ManualExecuteCommandZ = z.object({
  accountId: z.string().min(1).max(32),
  symbol: z.string().min(1).max(16),
  side: z.enum(["buy", "sell"]),
  lots: z.number().positive(),
  /**
   * SL is required. We refuse to stage a trade without one — the rest of
   * the pipeline (orphan close, drawdown tracker) assumes each open
   * ticket carries a floor.
   */
  sl: z.number().positive(),
  tp: z.number().positive().nullable().optional(),
  /** Max acceptable drift in points between the quote the operator
   *  saw and the quote the bot gets at fill time. Bot aborts if
   *  exceeded and acks with an error. Default 5. */
  slippage: z.number().int().positive().max(1000).optional(),
  comment: z.string().max(128).optional(),
});
export type ManualExecuteCommand = z.infer<typeof ManualExecuteCommandZ>;

export const ManualExecuteRequestZ = z.object({
  pairId: z.string().min(1).max(64),
  commands: z.array(ManualExecuteCommandZ).min(1).max(16),
});
export type ManualExecuteRequest = z.infer<typeof ManualExecuteRequestZ>;

export const ManualExecuteAckZ = z.object({
  accountId: z.string(),
  ok: z.boolean(),
  /** Broker-assigned ticket on success. Null until the bot reports back
   *  (or on timeout/error). */
  ticket: z.number().int().nullable().optional(),
  /** Open-fill price the driver observed, when reported. */
  fillPrice: z.number().nullable().optional(),
  /** Real driver/route error string when ok=false (broker rejection,
   *  bot offline, validation, etc). */
  error: z.string().nullable(),
});
export type ManualExecuteAck = z.infer<typeof ManualExecuteAckZ>;

export const ManualExecuteResponseZ = z.object({
  dispatched: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  acks: z.array(ManualExecuteAckZ),
});
export type ManualExecuteResponse = z.infer<typeof ManualExecuteResponseZ>;

// ── Pending orders ────────────────────────────────────────────────

export const PendingCreateRequestZ = z.object({
  pairId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  symbol: z.string().min(1).max(16),
  side: z.enum(["buy", "sell"]),
  lots: z.number().positive(),
  /** The price the operator wants to trigger at. */
  triggerPrice: z.number().positive(),
  /**
   * Which side of the price does the trigger fire on?
   *   'ge' — fire when bid >= triggerPrice (e.g. buy-stop / sell-limit)
   *   'le' — fire when ask <= triggerPrice (e.g. buy-limit / sell-stop)
   * We don't model the full MT5 order-type taxonomy — `side` + direction
   * covers every practical case for the dashboard's use.
   */
  triggerDirection: z.enum(["ge", "le"]),
  sl: z.number().positive(),
  tp: z.number().positive().nullable().optional(),
  slippage: z.number().int().positive().max(1000).optional(),
  /** Auto-expire the order after this many minutes. Default 24 h. */
  expiresInMin: z.number().int().positive().max(60 * 24 * 30).optional(),
});
export type PendingCreateRequest = z.infer<typeof PendingCreateRequestZ>;

export const PendingRowZ = z.object({
  id: z.string(),
  pairId: z.string(),
  accountId: z.string(),
  symbol: z.string(),
  side: z.enum(["buy", "sell"]),
  lots: z.number(),
  triggerPrice: z.number(),
  triggerDirection: z.enum(["ge", "le"]),
  sl: z.number(),
  tp: z.number().nullable(),
  slippage: z.number().int(),
  status: z.enum(["pending", "fired", "canceled", "expired"]),
  createdAt: z.number().int(),
  firedAt: z.number().int().nullable(),
  expiresAt: z.number().int(),
  firedTicket: z.number().int().nullable(),
});
export type PendingRow = z.infer<typeof PendingRowZ>;

export const PendingListResponseZ = z.object({
  pending: z.array(PendingRowZ),
});
export type PendingListResponse = z.infer<typeof PendingListResponseZ>;

// ── Close / modify ────────────────────────────────────────────────

export const ManualCloseRequestZ = z.object({
  accountId: z.string().min(1).max(32),
  ticket: z.union([z.number().int().positive(), z.literal("all")]),
});
export type ManualCloseRequest = z.infer<typeof ManualCloseRequestZ>;

/**
 * SL is required on modify — same invariant as execute. Every open
 * ticket in the system is supposed to carry a floor (drawdown tracker
 * and orphan-close assume it), so we refuse modifies that would leave
 * a position bare. TP stays nullable because news_protect legitimately
 * strips TP mid-event.
 */
export const ManualModifyRequestZ = z.object({
  accountId: z.string().min(1).max(32),
  ticket: z.number().int().positive(),
  sl: z.number().positive(),
  tp: z.number().positive().nullable(),
});
export type ManualModifyRequest = z.infer<typeof ManualModifyRequestZ>;

// ── Pair-level Open Hedge ─────────────────────────────────────────
//
// Operator clicks Open Hedge on /pairs/:slug/:symbol. The route triggers
// HedgePair.initiateTrade directly (no WS, no workflow) and polls the
// DO until both legs reach `opened` or the handshake aborts. Returns
// the broker tickets so the dashboard toast tells the truth.
//
// `slDistancePrice` / `tpDistancePrice` are price-unit distances; the
// dialog converts USD brackets to price-units via the contract size
// before sending.

export const ManualHedgeOpenRequestZ = z.object({
  pairId: z.string().min(1).max(64),
  symbol: z.string().min(1).max(16),
  lots: z.number().positive(),
  buyAccountId: z.string().min(1).max(32),
  sellAccountId: z.string().min(1).max(32),
  slDistancePrice: z.number().positive(),
  tpDistancePrice: z.number().positive(),
});
export type ManualHedgeOpenRequest = z.infer<typeof ManualHedgeOpenRequestZ>;

export const ManualHedgeOpenResponseZ = z.object({
  ok: z.boolean(),
  longTicket: z.number().int().nullable(),
  shortTicket: z.number().int().nullable(),
  /** Real driver/handshake error string when ok=false. */
  error: z.string().nullable().optional(),
  /** Per-leg final state when the handshake didn't fully complete —
   *  one of "pending" | "prepared" | "staged" | "opened" | "aborted". */
  longStatus: z.string().optional(),
  shortStatus: z.string().optional(),
});
export type ManualHedgeOpenResponse = z.infer<typeof ManualHedgeOpenResponseZ>;
